import { Button, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import { useAccess } from '../../../auth/useAccess.js';
import { useCompanySettingsMutation, useSettingsQuery } from '../useSettingsQuery.js';

/**
 * When the phone app's weekly "check your hours" opens: `weekCheckDay` (0 Sunday to 6 Saturday,
 * default 5, Friday) and `weekCheckTime` (HH:MM in the company zone, default 15:00). Only an owner
 * changes them; anyone else who can open Settings reads them. The card hides until the server sends
 * both keys, because the server refuses a key it does not know.
 *
 * It saves on its own rather than through the page's Save bar: company rules are owner-only on
 * the server, and the page's Save is open to anyone with settings.manage.
 */

const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60';

export function WeekCheckSettingsCard() {
  const { t } = useTranslation();
  const canEdit = useAccess()({ minRole: 'Admin' });
  const companySettings = useSettingsQuery().data?.companySettings;
  const storedDay = companySettings?.weekCheckDay;
  const storedTime = companySettings?.weekCheckTime;
  const save = useCompanySettingsMutation();
  const [day, setDay] = useState(typeof storedDay === 'number' ? storedDay : 5);
  const [time, setTime] = useState(typeof storedTime === 'string' ? storedTime : '15:00');

  useEffect(() => {
    if (typeof storedDay === 'number') setDay(storedDay);
    if (typeof storedTime === 'string') setTime(storedTime);
  }, [storedDay, storedTime]);

  if (typeof storedDay !== 'number' || typeof storedTime !== 'string') return null;

  const changed = day !== storedDay || time !== storedTime;
  // A cleared time input reads as ''; the server wants HH:MM, so there is nothing valid to send.
  const valid = /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('settings:weekCheck.heading')}
      </h2>
      <p className="mb-4 text-muted-foreground text-sm">
        {canEdit ? t('settings:weekCheck.description') : t('settings:weekCheck.ownersOnly')}
      </p>
      <fieldset disabled={!canEdit} className="grid min-w-0 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="s-week-check-day" className="font-medium text-foreground text-sm">
            {t('settings:weekCheck.day')}
          </label>
          <select
            id="s-week-check-day"
            className={INPUT}
            value={day}
            onChange={(e) => setDay(Number(e.target.value))}
          >
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {t(`settings:weekCheck.days.${d}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="s-week-check-time" className="font-medium text-foreground text-sm">
            {t('settings:weekCheck.time')}
          </label>
          <input
            id="s-week-check-time"
            type="time"
            step={60}
            className={INPUT}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            aria-describedby="s-week-check-time-hint"
          />
          <p id="s-week-check-time-hint" className="mt-1 text-muted-foreground text-xs">
            {t('settings:weekCheck.timeHint')}
          </p>
        </div>
      </fieldset>
      {canEdit ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {save.isError ? (
            <p role="alert" className="mr-auto text-destructive text-sm">
              {t('settings:weekCheck.saveError')}
            </p>
          ) : null}
          {save.isSuccess && !changed ? (
            <p role="status" className="mr-auto text-muted-foreground text-sm">
              {t('settings:weekCheck.saved')}
            </p>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            disabled={save.isPending || !changed || !valid}
            onClick={() => save.mutate({ weekCheckDay: day, weekCheckTime: time })}
          >
            {t('settings:weekCheck.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
